import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, MessageSquare, RefreshCw, Reply } from 'lucide-react';
import { COMM_DEPARTMENT_LABEL, formatDateTime } from '@nu/shared';
import { acknowledge, markRead, needsAck, refreshInbox, useInbox } from './inbox.js';
import MessageBlocks from './MessageBlocks.jsx';

// Messages: everything the office has sent you, newest first. /messages/:id
// opens one (the link in the push and the email), which marks it read.
export default function Inbox() {
  const { id } = useParams();
  const { messages, status } = useInbox();
  if (id) return <Reader id={Number(id)} messages={messages} status={status} />;

  const list = messages || [];
  const unread = list.filter((m) => !m.readAt).length;
  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Messages</h1>
            <p className="muted">{list.length ? `${unread ? `${unread} unread` : 'All read'}${list.filter(needsAck).length ? ` · ${list.filter(needsAck).length} to acknowledge` : ''}` : 'Updates from the office.'}</p>
          </div>
          <button type="button" className="btn icon-only" aria-label="Check for new messages" onClick={() => refreshInbox()}><RefreshCw size={18} aria-hidden="true" /></button>
        </div>
        {status === 'loading' && !messages && <p className="muted">Loading…</p>}
        {status === 'error' && !messages && <p className="notice-line notice-warn">Messages couldn&apos;t be loaded. They&apos;ll show when you have signal.</p>}
        {messages && !list.length && (
          <div className="empty">
            <MessageSquare size={36} aria-hidden="true" />
            <p><strong>No messages yet</strong></p>
            <p className="muted">Messages from the office will appear here. You&apos;ll also get a notification on your phone.</p>
          </div>
        )}
        {list.length > 0 && (
          <ul className="msg-list">
            {list.map((m) => (
              <li key={m.id}>
                <Link to={`/messages/${m.id}`} className={`msg-row${m.readAt ? '' : ' unread'}`}>
                  <span className="msg-dot" aria-hidden="true" />
                  <span className="msg-row-main">
                    <span className="msg-row-top">
                      <span className="msg-dept">{COMM_DEPARTMENT_LABEL[m.department] || 'Message'}</span>
                      <span className="small muted">{formatDateTime(m.sentAt)}</span>
                    </span>
                    <strong className="msg-subject">{m.subject}</strong>
                    <span className="msg-tags">
                      {!m.readAt && <span className="sr-only">Unread. </span>}
                      {m.priority === 'urgent' && <span className="status status-aborted">Urgent</span>}
                      {needsAck(m) && <span className="status status-paused">Acknowledge</span>}
                      {m.requiresAck && m.ackedAt && <span className="status status-completed">Acknowledged</span>}
                    </span>
                  </span>
                  <ChevronRight size={20} aria-hidden="true" className="muted" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Reader({ id, messages, status }) {
  const navigate = useNavigate();
  const m = messages?.find((x) => x.id === id);
  useEffect(() => { if (m) markRead(m); }, [m]);
  // A link to a message that isn't here yet (just sent): check once.
  useEffect(() => { if (messages && !m) refreshInbox(); }, [messages, m]);

  const back = (
    <button type="button" className="link-btn back-link" onClick={() => navigate('/messages')}>
      <ArrowLeft size={18} aria-hidden="true" /> Messages
    </button>
  );
  if (!m) {
    return (
      <section className="card">
        {back}
        <p className="muted">{status === 'ready' || status === 'error' ? 'This message isn’t in your inbox.' : 'Loading…'}</p>
      </section>
    );
  }
  return (
    <article className="card msg-reader">
      {back}
      <p className="msg-dept">{COMM_DEPARTMENT_LABEL[m.department] || 'Message'}{m.priority === 'urgent' && <span className="status status-aborted msg-urgent">Urgent</span>}</p>
      <h1 className="msg-title">{m.subject}</h1>
      <p className="small muted">From {m.sentByName} · {formatDateTime(m.sentAt)}{m.editedAt ? ` · Updated ${formatDateTime(m.editedAt)}` : ''}</p>
      {needsAck(m) && <p className="notice-line notice-warn">This message needs your acknowledgement after reading it.</p>}
      <MessageBlocks blocks={m.blocks} />
      {/* A platform message (e.g. a helpdesk reply) says where to answer: on the ticket, not here. */}
      {m.actionLink && (
        <div className="actions">
          <Link to={m.actionLink} className="btn primary with-icon step-btn">{m.actionLabel === 'Reply' && <Reply size={20} aria-hidden="true" />} {m.actionLabel}</Link>
        </div>
      )}
      {m.requiresAck && (
        <div className="msg-ack">
          {m.ackedAt
            ? <p className="ack-done"><Check size={18} aria-hidden="true" /> You acknowledged this on {formatDateTime(m.ackedAt)}.</p>
            : (
              <>
                <p className="small muted">By tapping below you confirm you&apos;ve read and understood this message.</p>
                <button type="button" className="btn primary with-icon step-btn" onClick={() => acknowledge(m)}><Check size={20} aria-hidden="true" /> I acknowledge</button>
              </>
            )}
        </div>
      )}
    </article>
  );
}

// On Home: messages still waiting for an acknowledgement. Never blocks; it
// can be put away until another one arrives.
const DISMISS_KEY = 'nu.ackReminder';
export function AckReminder() {
  const { messages } = useInbox();
  const waiting = (messages || []).filter(needsAck);
  const key = waiting.map((m) => m.id).join(',');
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY); } catch { return null; }
  });
  if (!waiting.length || dismissed === key) return null;
  const oldest = waiting[waiting.length - 1];
  const hide = () => {
    try { sessionStorage.setItem(DISMISS_KEY, key); } catch { /* fine */ }
    setDismissed(key);
  };
  return (
    <section className="card ack-reminder" role="status">
      <p><strong>{waiting.length === 1 ? '1 message needs' : `${waiting.length} messages need`} your acknowledgement.</strong></p>
      <div className="ack-reminder-actions">
        <Link to={`/messages/${oldest.id}`} className="btn primary">Open</Link>
        <button type="button" className="btn" onClick={hide}>Later</button>
      </div>
    </section>
  );
}
